/**
 * Created by Ning Jiang on 10/4/2018.
 */

import {MultiSprites} from "@nolimitcity/slot-game/bin/core/displayobject/MultiSprites";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {
    ISymbolStateConfig,
    SymbolStateTransition
} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Elastic, TimelineLite, TweenLite} from "gsap";
import {TombstoneReelSymbol} from "./TombstoneReelSymbol";

export class TombstoneWildSymbol extends TombstoneReelSymbol {

    private static _multiplierTopOffset:number = -40;
    private static _multiplierBottomOffset:number = 40;

    private _multiplierTop:MultiSprites | null;
    private _multiplierBottom:MultiSprites | null;
    private _currentMultiplier:number = 1;

    private _holesContainer:PIXI.Container;

    constructor(symName:string, reelId:number, state?:ISymbolStateConfig) {
        super(symName, reelId, state);
    }

    protected initAnimations():void {
        // When compensating reels, broken stacked symbol can be replaced with normal symbols.
        if(this.symName.indexOf("W") === -1) {
            this.removeMultiplier();
            return;
        }

        if(!this._multiplierTop) {
            this._multiplierTop = new MultiSprites({
                normal : {
                    textures : GameResources.getTextures("wildMulti"),
                    anchor : [0.5, 0.5]
                },
                spin : {
                    textures : GameResources.getTextures("wildMulti_spin"),
                    anchor : [0.5, 0.5]
                }
            });
            this._multiplierTop.position.set(0, -this.stackedSymName!.index * GameConfig.instance.SYMBOL_HEIGHT + TombstoneWildSymbol._multiplierTopOffset);
        }

        if(!this._multiplierBottom) {
            this._multiplierBottom = new MultiSprites({
                normal : {
                    textures : GameResources.getTextures("wildMulti"),
                    anchor : [0.5, 0.5]
                },
                spin : {
                    textures : GameResources.getTextures("wildMulti_spin"),
                    anchor : [0.5, 0.5]
                }
            });
            this._multiplierBottom.position.set(0, (2 - this.stackedSymName!.index) * GameConfig.instance.SYMBOL_HEIGHT + TombstoneWildSymbol._multiplierBottomOffset);
        }

        this._holesContainer = new PIXI.Container();
    }

    protected initState(stateConfig:ISymbolStateConfig):boolean {
        const result:boolean = super.initState(stateConfig);

        if(this._multiplierTop) {
            this.addChild(this._multiplierTop);
        }
        if(this._multiplierBottom) {
            this.addChild(this._multiplierBottom);
        }

        if(this._holesContainer) {
            this.addChild(this._holesContainer);
        }

        return result;
    }

    protected addNewStateAnimation(timeline:TimelineLite, newState:SymbolStateTransition, stateAnimation:TimelineSprite, delay:number):void {
        super.addNewStateAnimation(timeline, newState, stateAnimation, delay);

        if(this._multiplierBottom && this._multiplierTop) {
            if(newState.state.keyword === GameConfig.instance.SYMBOL_STATES.spin.keyword) {
                timeline.add(() => {this.updateMultiplier(this._currentMultiplier, false, "spin");}, delay);
                timeline.add(this._multiplierTop.playSprite("spin",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
                timeline.add(this._multiplierBottom.playSprite("spin",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
            } else {
                timeline.add(() => {this.updateMultiplier(this._currentMultiplier, false, "normal");}, delay);
                timeline.add(this._multiplierTop.playSprite("normal",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
                timeline.add(this._multiplierBottom.playSprite("normal",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
            }

            if(newState.state.keyword === GameConfig.instance.SYMBOL_STATES.noWin!.keyword) {
                for(let spriteName in this._multiplierTop.sprites) {
                    const toSprite:TimelineSprite = this._multiplierTop.sprites[spriteName];
                    timeline.add(TweenLite.to(toSprite, newState.fadeInDuration, {pixi : {tint : "#555555"}}), delay);
                    const bottomSprite:TimelineSprite = this._multiplierBottom.sprites[spriteName];
                    timeline.add(TweenLite.to(bottomSprite, newState.fadeInDuration, {pixi : {tint : "#555555"}}), delay);
                }
            } else {
                for(let spriteName in this._multiplierTop.sprites) {
                    const toSprite:TimelineSprite = this._multiplierTop.sprites[spriteName];
                    timeline.add(TweenLite.to(toSprite, newState.fadeInDuration, {pixi : {tint : "#ffffff"}}), delay);
                    const bottomSprite:TimelineSprite = this._multiplierBottom.sprites[spriteName];
                    timeline.add(TweenLite.to(bottomSprite, newState.fadeInDuration, {pixi : {tint : "#ffffff"}}), delay);
                }
            }
        }

        // no need to do more state, because there will be no other state when there are holes.
        if(newState.state.keyword === GameConfig.instance.SYMBOL_STATES.spin.keyword) {
            (<PIXI.Sprite[]>this._holesContainer.children).forEach((hole:PIXI.Sprite) => {
                hole.texture = GameResources.getTextures("bulletHole_spin")[0];
                hole.rotation = 0;
            })
        }
    }


    public updateMultiplier(multiplier:number, isAnimated:boolean, spriteName?:string):TimelineLite | null {
        const timeline:TimelineLite = new TimelineLite();
        (<TombstoneWildSymbol[]>this.stack!.symbols).forEach((wild:TombstoneWildSymbol) => {
            if(wild) {
                timeline.add(wild.updateMultiplierInternal(multiplier, wild === this ? isAnimated : false, spriteName), 0);
            }
        });

        return timeline;
    }

    private updateMultiplierInternal(multiplier:number, isAnimated:boolean, spriteName?:string):TimelineLite | null {
        this._currentMultiplier = multiplier;
        if(this._multiplierTop == null || this._multiplierBottom == null) {
            return null;
        }
        const index:number = this._currentMultiplier;
        let topSprite:TimelineSprite | undefined = undefined;
        let bottomSprite:TimelineSprite | undefined = undefined;
        if(spriteName == null) {
            if(this._multiplierTop.currentSprite) {
                // in restore state in super freespin, the multiplier is set before currentSprite.
                topSprite = this._multiplierTop.currentSprite;
            }
            if(this._multiplierBottom.currentSprite) {
                bottomSprite = this._multiplierBottom.currentSprite;
            }
        } else {
            topSprite = this._multiplierTop.sprites[spriteName];
            bottomSprite = this._multiplierBottom.sprites[spriteName];
        }

        const timeline:TimelineLite = new TimelineLite();
        if(topSprite) {
            timeline.add(topSprite.getAnimation([index, index]), 0);
            if(isAnimated) {
                timeline.add(TweenLite.fromTo(topSprite.scale, 0.4,
                    {x : 1.5, y : 1.5, ease : Elastic.easeIn.config(1, 1)},
                    {x : 1, y : 1}), 0);
            }
        }
        if(bottomSprite) {
            timeline.add(bottomSprite.getAnimation([index, index]), 0);
            if(isAnimated) {
                timeline.add(TweenLite.fromTo(bottomSprite.scale, 0.4,
                    {x : 1.5, y : 1.5, ease : Elastic.easeIn.config(1, 1)},
                    {x : 1, y : 1}), 0);
            }
        }

        return timeline;
    }

    public removeMultiplier():void {
        if(this._multiplierTop && this._multiplierTop.parent) {
            this.removeChild(this._multiplierTop);
        }
        this._multiplierTop = null;
        if(this._multiplierBottom && this._multiplierBottom.parent) {
            this.removeChild(this._multiplierBottom);
        }
        this._multiplierBottom = null;
    }

    public showBusted():void {

    }

    // TODO: at in stack?
    public addHole(symbolId:number, offsetX:number, offsetY:number):void {
        //this will always be the bottom symbol, so we can use the offset without checking stacked positions.
        const hole:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("bulletHole")[0]);
        hole.anchor.set(0.5, 0.5);
        hole.rotation = Math.random() * Math.PI * 2;
        hole.position.set(offsetX, (symbolId - 2) * GameConfig.instance.SYMBOL_HEIGHT + offsetY);
        this._holesContainer.addChild(hole);
    }
}